/**
 * Which of Planning's floating panels are open, and which of them may be open
 * together.
 *
 * Each dock used to keep its own open flag, so nothing stopped two of them
 * claiming the same pixels: on a tablet the AI operator drew over the ROUTE
 * drawer, at 768 the targeting and AI docks overlapped, and below 1440 the
 * Tutorial Center sat on ROUTE. The flags live here so opening one can close
 * whatever it would cover.
 */

import { create } from 'zustand';

import { useMinWidth, useViewport } from '@/mobile/useViewport';
import { useScreen } from '@/stores/workspaceStore';

export type PlanDock = 'route' | 'copilot' | 'attack' | 'tutorial';

/**
 * - `compact`: phone and tablet. Every panel is a drawer or sheet over the
 *   same map, so one at a time.
 * - `float`: desktop Training below 1440. ROUTE is a column, and the Tutorial
 *   Center takes the AI operator's slot beside it, so those two share.
 * - `column`: Training with the Tutorial Center in its own column. Planning
 *   is 388px narrower, and at 1440 the targeting and AI docks overlapped by
 *   322px, so they share.
 * - `full`: everything has its own place.
 */
export type DockLayout = 'compact' | 'float' | 'column' | 'full';

/** Below this the Tutorial Center cannot have a column of its own beside
 *  Planning's two (see TrainingView). */
export const TUTORIAL_COLUMN_MIN_WIDTH = 1440;

/** Two 344px docks and their gutters need 776px between the plan panels;
 *  beside the tutorial column that is a 1800px window. */
export const DOCKS_BESIDE_TUTORIAL_MIN_WIDTH = 1800;

export function docksConflict(a: PlanDock, b: PlanDock, layout: DockLayout): boolean {
  if (a === b) return false;
  if (layout === 'compact') return true;
  const pair = (x: PlanDock, y: PlanDock) => (a === x && b === y) || (a === y && b === x);
  if (layout === 'float') return pair('tutorial', 'copilot');
  if (layout === 'column') return pair('attack', 'copilot');
  return false;
}

/** Open `dock`, closing whatever it would overlap. Pure, for the store and its tests. */
export function openDock(open: PlanDock[], dock: PlanDock, layout: DockLayout): PlanDock[] {
  return [...open.filter((d) => d !== dock && !docksConflict(d, dock, layout)), dock];
}

/** After the layout changes (a window resized across a tier), keep the most
 *  recently opened of any pair that now collides. */
export function settleDocks(open: PlanDock[], layout: DockLayout): PlanDock[] {
  const kept: PlanDock[] = [];
  for (let i = open.length - 1; i >= 0; i -= 1) {
    const dock = open[i]!;
    if (!kept.some((k) => docksConflict(k, dock, layout))) kept.unshift(dock);
  }
  return kept;
}

interface PlanDockState {
  /** Oldest first. */
  open: PlanDock[];
  show: (dock: PlanDock, layout: DockLayout) => void;
  hide: (dock: PlanDock) => void;
  toggle: (dock: PlanDock, layout: DockLayout) => void;
  settle: (layout: DockLayout) => void;
  reset: (open?: PlanDock[]) => void;
}

export const usePlanDocks = create<PlanDockState>((set) => ({
  open: [],
  show: (dock, layout) => set((state) => ({ open: openDock(state.open, dock, layout) })),
  hide: (dock) => set((state) => ({ open: state.open.filter((d) => d !== dock) })),
  toggle: (dock, layout) =>
    set((state) => ({
      open: state.open.includes(dock) ? state.open.filter((d) => d !== dock) : openDock(state.open, dock, layout),
    })),
  settle: (layout) =>
    set((state) => {
      const open = settleDocks(state.open, layout);
      return open.length === state.open.length ? state : { open };
    }),
  reset: (open = []) => set({ open }),
}));

export function useTutorialFloats(): boolean {
  const tier = useViewport();
  const roomy = useMinWidth(TUTORIAL_COLUMN_MIN_WIDTH);
  return tier === 'phone' || tier === 'tablet' || !roomy;
}

export function useDockLayout(): DockLayout {
  const tier = useViewport();
  const floats = useTutorialFloats();
  const training = useScreen() === 'training';
  const docksFit = useMinWidth(DOCKS_BESIDE_TUTORIAL_MIN_WIDTH);
  if (tier === 'phone' || tier === 'tablet') return 'compact';
  if (!training) return 'full';
  if (floats) return 'float';
  return docksFit ? 'full' : 'column';
}

/** Open state and a toggle for one dock, already bound to the current layout. */
export function usePlanDock(dock: PlanDock): { open: boolean; toggle: () => void; setOpen: (open: boolean) => void } {
  const layout = useDockLayout();
  const open = usePlanDocks((state) => state.open.includes(dock));
  const toggle = () => usePlanDocks.getState().toggle(dock, layout);
  const setOpen = (value: boolean) => {
    const store = usePlanDocks.getState();
    if (value) store.show(dock, layout);
    else store.hide(dock);
  };
  return { open, toggle, setOpen };
}
